import { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Popconfirm, Select, Table, Tag, message } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useManagementStore } from "@/stores/management";
import { agentService, type AgentInfo } from "@/services/agent";
import { providerService, type MyModelInfo } from "@/services/provider";
import { mcpService, type McpServer } from "@/services/mcp";
import { skillService, type SkillDef } from "@/services/skill";
import { useCardExtra } from "@/layouts/ManagementLayout";
import { formatDateTime } from "@/utils/date";

export function AgentManagement() {
  const { t } = useTranslation("management");
  const { context, hasPermission, permissions } = useManagementStore();
  const orgId = context?.type === "org" ? context.org.id : "";
  const canCreate = hasPermission("AgentCreate");

  const [agents, setAgents] = useState<AgentInfo[]>([]);
  const [myModels, setMyModels] = useState<MyModelInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState<AgentInfo | null>(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const selectedAdapter = Form.useWatch("adapter_type", form);

  // Org-level MCP and Skill lists for binding selects
  const [orgMcps, setOrgMcps] = useState<McpServer[]>([]);
  const [orgSkills, setOrgSkills] = useState<SkillDef[]>([]);
  // Currently bound IDs (loaded when editing)
  const [boundMcpIds, setBoundMcpIds] = useState<string[]>([]);
  const [boundSkillIds, setBoundSkillIds] = useState<string[]>([]);

  const load = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [agentList, models, mcps, skills] = await Promise.all([
        agentService.list(orgId),
        providerService.myModels(),
        mcpService.list(orgId).catch(() => []),
        skillService.list(orgId).catch(() => []),
      ]);
      setAgents(agentList);
      setMyModels(models);
      setOrgMcps(mcps);
      setOrgSkills(skills);
    } catch { setAgents([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [orgId]);

  // Filter and deduplicate models by selected adapter type
  const filteredProviders = myModels.filter((m) => !selectedAdapter || m.adapter_type === selectedAdapter);
  const modelSet = new Set<string>();
  const availableModels: { label: string; value: string }[] = [];
  for (const p of filteredProviders) {
    for (const model of p.models) {
      if (model !== "*" && !modelSet.has(model)) {
        modelSet.add(model);
        availableModels.push({ label: model, value: model });
      }
    }
  }

  useCardExtra(
    canCreate ? (
      <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => {
        setEditingAgent(null);
        form.resetFields();
        form.setFieldsValue({ adapter_type: "ClaudeCode", visibility_policy: "OrgWide", context_window: 128000 });
        setBoundMcpIds([]);
        setBoundSkillIds([]);
        setModalOpen(true);
      }}>
        {t("agents.create")}
      </Button>
    ) : null,
    [t, permissions],
  );

  const openEdit = async (agent: AgentInfo) => {
    setEditingAgent(agent);
    form.setFieldsValue(agent);
    // Load current bindings
    try {
      const [agentMcps, agentSkills] = await Promise.all([
        agentService.listAgentMcps(agent.id).catch(() => []),
        agentService.listAgentSkills(agent.id).catch(() => []),
      ]);
      const mcpIds = agentMcps.map((m) => m.id);
      const skillIds = agentSkills.map((s) => s.id);
      setBoundMcpIds(mcpIds);
      setBoundSkillIds(skillIds);
      form.setFieldsValue({ bound_mcps: mcpIds, bound_skills: skillIds });
    } catch {
      setBoundMcpIds([]);
      setBoundSkillIds([]);
    }
    setModalOpen(true);
  };

  const syncBindings = async (agentId: string, newMcpIds: string[], newSkillIds: string[], oldMcpIds: string[], oldSkillIds: string[]) => {
    // MCP: unbind removed, bind added
    const mcpsToUnbind = oldMcpIds.filter((id) => !newMcpIds.includes(id));
    const mcpsToBind = newMcpIds.filter((id) => !oldMcpIds.includes(id));
    // Skills: unbind removed, bind added
    const skillsToUnbind = oldSkillIds.filter((id) => !newSkillIds.includes(id));
    const skillsToBind = newSkillIds.filter((id) => !oldSkillIds.includes(id));

    await Promise.all([
      ...mcpsToUnbind.map((id) => agentService.unbindMcp(agentId, id)),
      ...mcpsToBind.map((id) => agentService.bindMcp(agentId, id)),
      ...skillsToUnbind.map((id) => agentService.unbindSkill(agentId, id)),
      ...skillsToBind.map((id) => agentService.bindSkill(agentId, id)),
    ]);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const newMcpIds: string[] = values.bound_mcps || [];
    const newSkillIds: string[] = values.bound_skills || [];
    // Remove binding fields from agent payload
    const { bound_mcps, bound_skills, ...agentPayload } = values;

    setSaving(true);
    try {
      if (editingAgent) {
        await agentService.update(editingAgent.id, agentPayload);
        await syncBindings(editingAgent.id, newMcpIds, newSkillIds, boundMcpIds, boundSkillIds);
      } else {
        const created = await agentService.create(orgId, agentPayload);
        // For new agent, bind all selected (no old bindings)
        await syncBindings(created.id, newMcpIds, newSkillIds, [], []);
      }
      message.success(t("common:success"));
      setModalOpen(false);
      load();
    } catch (e) { message.error(e instanceof Error ? e.message : t("common:error")); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try {
      await agentService.delete(id);
      message.success(t("common:success"));
      load();
    } catch { message.error(t("common:error")); }
  };

  const columns = [
    { title: t("agents.table.name"), dataIndex: "name", key: "name" },
    { title: t("agents.table.adapter"), dataIndex: "adapter_type", key: "adapter" },
    { title: t("agents.table.model"), dataIndex: "model", key: "model" },
    {
      title: t("agents.table.visibility"), dataIndex: "visibility_policy", key: "visibility",
      render: (v: string) => <Tag color={v === "OrgWide" ? "blue" : "orange"}>{v}</Tag>,
    },
    {
      title: t("agents.table.created_at"), dataIndex: "created_at", key: "created_at",
      render: (v: string) => formatDateTime(v),
    },
    ...(canCreate ? [{
      title: t("common:action"), key: "action", width: 100,
      render: (_: unknown, record: AgentInfo) => (
        <span style={{ display: "flex", gap: 4 }}>
          <Button type="text" icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Popconfirm title={t("agents.delete.confirm")} onConfirm={() => handleDelete(record.id)}>
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </span>
      ),
    }] : []),
  ];

  return (
    <>
      <Table dataSource={agents} columns={columns} rowKey="id" loading={loading} size="middle" />
      <Modal
        title={editingAgent ? t("common:edit") : t("agents.create")}
        open={modalOpen} onOk={handleSubmit} onCancel={() => setModalOpen(false)}
        confirmLoading={saving} width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item label={t("agents.table.name")} name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t("agents.form.adapter_type")} name="adapter_type" rules={[{ required: true }]}>
            <Select options={[{ label: "Claude Code", value: "ClaudeCode" }, { label: "Codex", value: "Codex" }]} />
          </Form.Item>
          <Form.Item label={t("agents.table.model")} name="model" rules={[{ required: true }]}>
            <Select
              showSearch
              placeholder={t("agents.form.select_model")}
              options={availableModels}
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
              }
            />
          </Form.Item>
          <Form.Item label={t("agents.form.system_prompt")} name="system_prompt">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label={t("agents.form.working_directory")} name="working_directory">
            <Input placeholder={t("agents.form.working_directory_hint")} />
          </Form.Item>
          <Form.Item label={t("agents.table.visibility")} name="visibility_policy">
            <Select options={[
              { label: t("agents.visibility.org_wide"), value: "OrgWide" },
              { label: t("agents.visibility.restricted"), value: "Restricted" },
            ]} />
          </Form.Item>
          <Form.Item label={t("agents.form.bound_mcps")} name="bound_mcps">
            <Select
              mode="multiple"
              placeholder={t("agents.form.select_mcps")}
              options={orgMcps.map((m) => ({ label: m.name, value: m.id }))}
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
              }
            />
          </Form.Item>
          <Form.Item label={t("agents.form.bound_skills")} name="bound_skills">
            <Select
              mode="multiple"
              placeholder={t("agents.form.select_skills")}
              options={orgSkills.map((s) => ({ label: s.name, value: s.id }))}
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
